'use client';

import React from 'react';
import type { ActivityLogEntry } from 'src/types';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import useFeatureFlags from 'src/hooks/use-feature-flags';
import { updateVisibility } from 'src/redux/modals/slice';

interface ResidentCausedEventCardProps {
	logDetails: ActivityLogEntry;
}

const RESIDENT_CAUSED_EVENTS = [
	'job.resident_caused.set',
	'job.resident_caused.edited',
	'job.resident_caused.deleted',
];

const responsibilityLabel = (value: string | null | undefined): string => {
	if (value === 'partially') return 'Partially Responsible';
	if (value === 'yes') return 'Fully Responsible';
	return '';
};

const changedByLabel = (changedBy: string | undefined): string =>
	changedBy === 'admin' ? 'Lula' : 'You';

const ResidentCausedEventCard: React.FC<ResidentCausedEventCardProps> = ({
	logDetails,
}) => {
	const { residentResponsibilityEdit } = useFeatureFlags();
	const dispatch = useAppDispatch();
	const activityLogData = useAppSelector(
		(state) =>
			(state?.jobDetails?.activityLog as Record<string, unknown>)?.data as
				| ActivityLogEntry[]
				| undefined,
	);
	const slideOutType = useAppSelector((state) => state?.jobDetails?.slideOut?.type);
	const masterStatus = useAppSelector((state) => state?.jobDetails?.data?.master_status);
	const jobs = useAppSelector((state) => state?.jobDetails?.data?.jobs);

	const latestResidentCausedEntry = activityLogData?.find((entry) =>
		RESIDENT_CAUSED_EVENTS.includes(entry.event),
	);

	const isLatestActionable =
		latestResidentCausedEntry?.activity_uuid === logDetails.activity_uuid &&
		logDetails.event !== 'job.resident_caused.deleted';

	const attrs = logDetails.event_attributes;
	const residentCauseStatus = attrs.resident_cause_status;
	const changedBy = changedByLabel(attrs?.changed_by);

	const isEditableStatus = (() => {
		if (slideOutType === 'jobDetails') {
			return masterStatus?.slug === 'invoice_ready' || masterStatus?.slug === 'closed';
		}
		if (slideOutType === 'serviceRequestDetails') {
			const matchingJob = jobs?.find((job) => job.root_job_id === Number(logDetails.job_id));
			return matchingJob?.status?.slug === 'invoice_ready' || matchingJob?.status?.slug === 'closed';
		}
		return false;
	})();

	const onClickDelete = () => {
		dispatch(
			updateVisibility({
				modalType: 'residentResponsibilityRemoveModal',
				isVisible: true,
				extraProps: { rootNodeId: logDetails.job_id, jobId: attrs.job_id },
			}),
		);
	};

	const onClickEdit = () => {
		dispatch(
			updateVisibility({
				modalType: 'residentResponsibilityModal',
				isVisible: true,
				extraProps: {
					mode: 'edit',
					rootNodeId: logDetails.job_id,
					jobId: attrs.job_id,
					reason: attrs.reason,
          residentCauseStatus,
				},
			}),
		);
	};

	const actionButtons = isLatestActionable && residentResponsibilityEdit && isEditableStatus ? (
		<div className="resident-responsibility-alert__actions mt-2">
			<button
				type="button"
				className="grey-outline-btn"
				onClick={onClickDelete}
			>
				Delete
			</button>
			<button type="button" className="grey-outline-btn" onClick={onClickEdit}>
				Edit
			</button>
		</div>
	) : null;

	switch (logDetails.event) {
		case 'job.resident_caused.set':
			return (
				<React.Fragment>
					<p className="mb-0">
						<strong>{changedBy} marked this Work Order Resident Caused</strong>
					</p>
					<p className="mb-0">
						{responsibilityLabel(residentCauseStatus)} |{' '}
						{attrs?.reason as string}
					</p>
					{actionButtons}
				</React.Fragment>
			);

		case 'job.resident_caused.edited':
			return (
				<React.Fragment>
					<p className="mb-0">
						<strong>
							{changedBy} edited the Resident Caused determination
						</strong>
					</p>
					<p className="mb-0">
						{responsibilityLabel(residentCauseStatus)} |{' '}
						{attrs?.reason as string}
					</p>
					{attrs?.reason_for_change ? (
						<p className="mb-0">Reason for Change: {attrs.reason_for_change}</p>
					) : null}
					{actionButtons}
				</React.Fragment>
			);

		case 'job.resident_caused.deleted':
			return (
				<React.Fragment>
					<p className="mb-0">
						<strong>{changedBy} changed this to Not Resident Caused</strong>
					</p>
					{attrs?.reason_for_change ? (
						<p className="mb-0">Reason for Change: {attrs.reason_for_change}</p>
					) : null}
				</React.Fragment>
			);

		default:
			return null;
	}
};

export default ResidentCausedEventCard;
